import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';

import { AppState } from 'src/app/store';
import { selectParticipantId } from 'src/app/features/session/store/selectors/session.selectors';
import { ERROR_MESSAGES } from 'src/app/features/session/constants/error-messages';
import { CaptureType } from '../components/id-verfication/capture-type.enum';
import { MisnapService } from '../services/misnap/misnap.service';
import { FrameCaptureResult } from '../services/misnap/responses/frame-capture-result.model';
import { FrameProcessingFeedback } from '../services/misnap/responses/frame-processing-feedback.model';
import { SdkError } from '../services/misnap/responses/sdk-error.model';
import { IdVerificationService } from '../services/id-verification.service';


interface IdVerificationState {
  captureErrorCode: string | undefined;
  currentCaptureType: CaptureType;
  idFrontImage: string | undefined;
  selfieImage: string | undefined;
  isCapturing: boolean;
  autoCaptureFeedbackKey: string | undefined;
  sdkErrorCode: number | undefined;
  isFlowComplete: boolean;
}


const initialState: IdVerificationState = {
  captureErrorCode: undefined,
  currentCaptureType: CaptureType.Selfie,
  idFrontImage: undefined,
  selfieImage: undefined,
  isCapturing: false,
  autoCaptureFeedbackKey: undefined,
  sdkErrorCode: undefined,
  isFlowComplete: false,
};

export const IdVerificationStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    isImageCaptured: computed(() =>
      store.currentCaptureType() === CaptureType.Selfie
        ? store.selfieImage() !== undefined
        : store.idFrontImage() !== undefined,
    ),
    imageToReview: computed(
      () =>
        (store.currentCaptureType() === CaptureType.Selfie
          ? store.selfieImage()
          : store.idFrontImage()) ?? '',
    ),
  })),
  withMethods(
    (
      store,
      misnapService = inject(MisnapService),
      idVerificationService = inject(IdVerificationService),
      appStore = inject(Store<AppState>),
    ) => ({
      _handleMisnapCaptureResult(result: FrameCaptureResult): void {
        const image = result.response.imageData || result.response.failedImage;
        const errorCode = result.response?.warnings?.key;

        const currentCaptureType = store.currentCaptureType();
        if (currentCaptureType === CaptureType.Selfie) {
          patchState(store, {
            selfieImage: image,
            captureErrorCode: errorCode,
            isCapturing: false,
          });
        } else if (currentCaptureType === CaptureType.IdFront) {
          patchState(store, {
            idFrontImage: image,
            captureErrorCode: errorCode,
            isCapturing: false,
          });
        }
      },
      _handleMisnapFeedback(feedback: FrameProcessingFeedback): void {
        patchState(store, { autoCaptureFeedbackKey: feedback.key });
      },
      _handleMisnapError(error: SdkError): void {
        patchState(store, {
          sdkErrorCode: error.code,
          isCapturing: false,
        });
      },
      _registerMisnapEventHandlers(): void {
        misnapService.registerCaptureResultHandler(
          this._handleMisnapCaptureResult.bind(this),
        );
        misnapService.registerFeedbackHandler(
          this._handleMisnapFeedback.bind(this),
        );
        misnapService.registerErrorHandler(this._handleMisnapError.bind(this));
      },
      async startAutoCapture(): Promise<void> {
        patchState(store, {
          isCapturing: true,
          sdkErrorCode: undefined,
        });

        this._registerMisnapEventHandlers();
        await misnapService.startAutoCapture(
          store.currentCaptureType(),
          'misnap-container',
          'misnap-video',
        );
      },
      stopAutoCapture(): void {
        misnapService.stopSDK();
      },
      async acceptCaptureResult(): Promise<void> {
        patchState(store, {
          captureErrorCode: undefined,
          autoCaptureFeedbackKey: undefined,
          sdkErrorCode: undefined,
        });

        const participantId = appStore.selectSignal(selectParticipantId)();

        if (!participantId) {
          throw new Error(ERROR_MESSAGES.PARTICIPANT_ID_NOT_AVAILABLE);
        }

        const currentCaptureType = store.currentCaptureType();

        if (currentCaptureType === CaptureType.Selfie) {
          const selfieImage = store.selfieImage();
          if (selfieImage) {
            await firstValueFrom(
              idVerificationService.createUserSnapshot(
                participantId,
                selfieImage,
              ),
            );
          }
          patchState(store, { currentCaptureType: CaptureType.IdFront });
        } else if (currentCaptureType === CaptureType.IdFront) {
          const idFrontImage = store.idFrontImage();
          if (idFrontImage) {
            await firstValueFrom(
              idVerificationService.createLicenseSnapshot(
                participantId,
                idFrontImage,
              ),
            );
          }
          patchState(store, { isFlowComplete: true });
        }
      },
      retakePhoto(): void {
        patchState(store, {
          captureErrorCode: undefined,
          autoCaptureFeedbackKey: undefined,
          sdkErrorCode: undefined,
        });

        const currentCaptureType = store.currentCaptureType();
        if (currentCaptureType === CaptureType.Selfie) {
          patchState(store, {
            selfieImage: undefined,
          });
        } else if (currentCaptureType === CaptureType.IdFront) {
          patchState(store, {
            idFrontImage: undefined,
          });
        }
      },
    }),
  ),
);
